import { useCallback } from "react";
import {
  createAdminDropdown,
  createAdminDropdownOptions,
  createAdminRole,
  createAdminRoleMap,
  deleteAdminDropdown,
  deleteAdminDropdownOption,
  getAdminDropdownOptions,
  getAdminDropdowns,
  getAdminRoleMap,
  getAdminRoles,
  updateAdminDropdown,
  updateAdminDropdownOption,
  updateAdminDropdownOptionStatus,
} from "../Utils/apiServices";

const normalizeError = (err) =>
  err?.error ||
  err?.message ||
  err?.response?.data?.message ||
  "Something went wrong. Please try again.";

const unwrap = (response) => {
  // axiosInstance returns: { success: true, data: <server_payload> }
  // Some APIs may return nested: { success, data: { success, data: ... } }
  let payload = response?.data;
  if (payload?.data?.data !== undefined) return payload.data.data;
  if (payload?.data !== undefined) return payload.data;
  return payload;
};

const unwrapList = (response) => {
  const payload = unwrap(response);
  if (Array.isArray(payload)) return payload;
  // Common API shape for dropdown options: { field, label, options: [...] }
  if (Array.isArray(payload?.options)) return payload.options;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  // Nested options shape: { data: { options: [...] } }
  if (Array.isArray(payload?.data?.options)) return payload.data.options;
  return [];
};

export const useMasterDataApi = () => {
  const listRoles = useCallback(async () => {
    const res = await getAdminRoles();
    return unwrapList(res);
  }, []);

  const createRole = useCallback(async (payload) => {
    const res = await createAdminRole(payload);
    return unwrap(res);
  }, []);

  const listDropdownFields = useCallback(async (includeInactive = false) => {
    const res = await getAdminDropdowns(includeInactive);
    return unwrapList(res);
  }, []);

  const createDropdownField = useCallback(async (payload) => {
    const res = await createAdminDropdown(payload);
    return unwrap(res);
  }, []);

  const updateDropdownField = useCallback(async (key, payload) => {
    const res = await updateAdminDropdown(key, payload);
    return unwrap(res);
  }, []);

  const deleteDropdownField = useCallback(async (key) => {
    const res = await deleteAdminDropdown(key);
    return unwrap(res);
  }, []);

  const listDropdownOptions = useCallback(async (dropdownKey, { includeInactive = false } = {}) => {
    const res = await getAdminDropdownOptions(dropdownKey, includeInactive);
    return unwrapList(res);
  }, []);

  // Expects backend payload: { dropdownKey, values: [] }
  const createDropdownOptions = useCallback(async ({ dropdownKey, values }) => {
    const res = await createAdminDropdownOptions({ dropdownKey, values });
    return unwrap(res);
  }, []);

  const updateDropdownOption = useCallback(async (optionId, payload) => {
    const res = await updateAdminDropdownOption(optionId, payload);
    return unwrap(res);
  }, []);

  const deleteDropdownOption = useCallback(async (optionId) => {
    const res = await deleteAdminDropdownOption(optionId);
    return unwrap(res);
  }, []);

  // Toggle option active state (uses PUT by optionId when available)
  const setDropdownOptionStatus = useCallback(async ({ optionId, dropdownKey, value, enabled }) => {
    if (optionId != null && optionId !== "") {
      const res = await updateAdminDropdownOption(optionId, { is_active: enabled });
      return unwrap(res);
    }
    const res = await updateAdminDropdownOptionStatus({ dropdownKey, value, enabled });
    return unwrap(res);
  }, []);

  const getRoleMap = useCallback(async ({ roleName, dropdownKey }) => {
    const res = await getAdminRoleMap({ roleName, dropdownKey });
    return unwrap(res);
  }, []);

  // Expects backend payload: { roleName, dropdownKey, optionValues: [] }
  const saveRoleMap = useCallback(async ({ roleName, dropdownKey, optionValues }) => {
    const res = await createAdminRoleMap({ roleName, dropdownKey, optionValues });
    return unwrap(res);
  }, []);

  return {
    normalizeError,
    listRoles,
    createRole,
    listDropdownFields,
    createDropdownField,
    updateDropdownField,
    deleteDropdownField,
    listDropdownOptions,
    createDropdownOptions,
    updateDropdownOption,
    deleteDropdownOption,
    setDropdownOptionStatus,
    getRoleMap,
    saveRoleMap,
  };
};

